import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { getBigAssetPath } from '@aytm/lib/common/assets'
import { EcommerceBannerPlacement, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { hasDiscount } from '../../utils/hasDiscount.ts'
import { useChildrenProducts } from '../../hooks/useChildrenProducts.tsx'
import { useBannerImpression } from '../../hooks/useBannerImpression.ts'
import style from './Banner.module.scss'

interface IBannerProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
}

// What the shopper pays: the Clubcard price when there is one.
const lowestPrice = (answer: IEcommerceAnswerSchema) => (hasDiscount(answer) ? answer.discounted_price : answer.price)

// The band is one creative promoting one product; the page that places it decides whether it is shown.
const Banner = ({ schema, answer, onItemSelect }: IBannerProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const family = useChildrenProducts({ answer, schema })
  const bannerRef = useBannerImpression(answer.id, 'banner')
  const image = schema.ad_image2

  // A family advertises its cheapest member, Clubcard prices included; a lone product its own price.
  const priceText =
    family.length > 1
      ? t<(amount: string) => string>(
          'questions',
          'ec',
          'tesco',
          'priceFrom'
        )(price(Math.min(...family.map(lowestPrice))))
      : price(answer.price)

  const imageElement = (
    <img src={getBigAssetPath(image)} alt={image.alt_text || t('questions', 'img', 'noAlt')} className={style.image} />
  )
  const arrow = (
    <span className={style.arrow}>
      <i className="pi pi-angle-right" />
    </span>
  )

  // The app's band is the creative alone with the arrow on it; the price column is the desktop's.
  if (viewport.mobile) {
    return (
      <div
        ref={bannerRef}
        className={`${style.banner} ${style.mobile}`}
        onClick={() => onItemSelect(answer.index, 'banner')}
        data-testid="SW-EC-Banner"
      >
        {imageElement}
        {arrow}
      </div>
    )
  }

  return (
    <div
      ref={bannerRef}
      className={style.banner}
      onClick={() => onItemSelect(answer.index, 'banner')}
      data-testid="SW-EC-Banner"
    >
      {imageElement}
      <div className={style.offer}>
        {hasDiscount(answer) && arrow}
        <div className={style.price} dangerouslySetInnerHTML={{ __html: priceText }} />
        {hasDiscount(answer) && (
          <div className={style.clubcard}>
            <div className={style.clubcardValue} dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }} />
            <div className={style.clubcardLabel}>{t('questions', 'ec', 'tesco', 'clubcardPrice')}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Banner
